/**
 * Der Rumpf einer neuen Nachricht — **an einer Stelle zusammengebaut.**
 *
 * Es gibt zwei Wege zu `POST /api/trips/{id}/messages`: die Eingabezeile
 * (`hooks/queries.ts`, `useSendMessage`) und der Ausgangskorb
 * (`lib/outbox.ts`), der nachholt, was ohne Netz geschrieben wurde. Beide
 * bauten denselben Rumpf, jeder für sich — und in `CLAUDE.md` steht, was das
 * heißt: *zweimal dieselbe Regel läuft auseinander.* Genau das ist bei den
 * Reaktionen und bei den Medien je Rolle schon zweimal passiert.
 *
 * Aufgefallen ist es beim Anbau der Absenderuhr (#71): das Feld hätte an zwei
 * Stellen dazugemusst, und eine hätte es früher oder später nicht mitbekommen.
 */
import { versatzDesGeraets } from '../lib/ortszeit'

export interface NachrichtRumpf {
  body: string
  media_ids: number[]
  client_key?: string
  reply_to_id?: number
  reply_to_media_id?: number
  tz_offset_min: number
  /** Into the draft chat (#85), and behind which post -- only when asked. */
  draft?: boolean
  after_id?: number
}

/**
 * **Die Uhr gehört zum Schreiben, nicht zum Ankommen** (#71).
 *
 * Der Ausgangskorb kann eine Nachricht Stunden später abschicken, und
 * womöglich aus einer anderen Zone — wer abends über die Grenze fährt und dann
 * erst Netz hat, hätte sonst die Uhr des Ankommens am Text stehen. Deshalb
 * gibt der Korb seinen **gemerkten** Versatz mit; wo keiner mitkommt, ist
 * Schreiben und Senden derselbe Augenblick, und die Uhr des Geräts ist die
 * richtige Antwort.
 *
 * Dieselbe Falle gab es am Highlight schon einmal — daher der Satz „Der
 * Ausgangskorb nimmt die Uhr des Punktes mit".
 */
export function nachrichtRumpf(
  teile: {
    body: string
    mediaIds?: number[]
    clientKey?: string
    replyToId?: number
    replyToMediaId?: number
    /** The draft chat (#85): the post waits instead of going out. */
    entwurf?: boolean
    /** And where it goes in the draft -- behind this one, or at the end. */
    nachId?: number | null
  },
  gemerkterVersatz?: number,
): NachrichtRumpf {
  return {
    // Both ways (the line and the outbox) carry the draft the same way --
    // that is what this one place is for.
    ...(teile.entwurf ? { draft: true, after_id: teile.nachId ?? undefined } : {}),
    body: teile.body,
    media_ids: teile.mediaIds ?? [],
    // Damit dieselbe Nachricht nicht zweimal im Bericht steht, wenn der
    // Versuch scheiterte, ohne dass er scheiterte -- eine verlorene Antwort
    // sieht wie ein Fehlschlag aus, ist aber keiner.
    client_key: teile.clientKey,
    reply_to_id: teile.replyToId,
    reply_to_media_id: teile.replyToMediaId,
    tz_offset_min: gemerkterVersatz ?? versatzDesGeraets(),
  }
}
